import React, { useEffect, useRef } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';

// Declare Google Maps types
declare global {
  interface Window {
    google: any;
    googleMapsLoaded: boolean;
  }
}

// Google Maps loader function
const loadGoogleMaps = (): Promise<void> => {
  if (window.google && window.googleMapsLoaded) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    return new Promise((resolve) => {
      const checkLoaded = () => {
        if (window.google && window.googleMapsLoaded) {
          resolve();
        } else {
          setTimeout(checkLoaded, 100);
        }
      };
      checkLoaded();
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const apiKey = 'AIzaSyBShSyYr2n-mrs2b1J23rt-93s3FNgVlOs';
    
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      window.googleMapsLoaded = true;
      resolve();
    };
    
    script.onerror = () => {
      reject(new Error('Failed to load Google Maps API'));
    };
    
    document.head.appendChild(script);
  });
};

const SimpleMap: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initializeMap = () => {
      if (window.google && window.google.maps && mapRef.current) {
        try {
          // Use Geocoding to get exact coordinates for BA1 3NS
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: 'BA1 3NS, UK' }, (results: any, status: any) => {
            if (status === 'OK' && results[0]) {
              const location = results[0].geometry.location;
              
              // Create map centered on the geocoded location
              const map = new window.google.maps.Map(mapRef.current, {
                zoom: 18,
                center: location,
                mapTypeId: 'roadmap'
              });

              // Add marker for BA1 3NS
              new window.google.maps.Marker({
                position: location,
                map: map,
                title: 'BA1 3NS'
              });
            } else {
              console.error('Geocoding failed:', status);
              // Fallback to approximate coordinates
              new window.google.maps.Map(mapRef.current, {
                zoom: 15,
                center: { lat: 51.3848, lng: -2.3602 },
                mapTypeId: 'roadmap'
              });
            }
          });
        } catch (error) {
          console.error('Error initializing map:', error);
        }
      }
    };

    // Load Google Maps API
    loadGoogleMaps()
      .then(() => {
        // Wait a bit more for the API to fully initialize
        setTimeout(() => {
          initializeMap();
        }, 500);
      })
      .catch((error) => {
        console.error('Failed to load Google Maps:', error);
      });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-6">
          <MapPinIcon className="h-8 w-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Simple Map Test</h1>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Postcode: BA1 3NS</h2>
          <div 
            ref={mapRef} 
            className="w-full h-96 rounded-lg border border-gray-300"
            style={{ minHeight: '400px' }}
          />
        </div>
      </div>
    </div>
  );
};

export default SimpleMap;
